import { Box, HStack, IconButton, Spinner, Text } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../design-system/button';
import { XOutline } from '../../../../design-system/icon';
import { DimensionKey } from '../../../../queries/recognition-analysis';
import { ReactComponent as AiSparkle } from '../ai-sparkle.svg';

export type DraftAssistCardProps = {
	suggestion: string;
	dimensionKey: DimensionKey;
	isLoading: boolean;
	onAdd: () => void;
	onDismiss: () => void;
};

const LABEL_MAPPING: Record<string, string> = {
	[DimensionKey.Personal]: 'labelPersonal',
	[DimensionKey.Contextual]: 'labelContextual',
	[DimensionKey.Specific]: 'labelSpecific',
	[DimensionKey.Genuine]: 'labelGenuine',
};

export const DraftAssistCard: FC<DraftAssistCardProps> = ({ suggestion, dimensionKey, isLoading, onAdd, onDismiss }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge.draftAssist' });

	return (
		<Box bg="hashtag.bgAlpha" borderWidth="1px" borderColor="hashtag.bgAlphaHover" borderRadius="md" p={3} my={2} data-testid="draft-assist-card">
			{/* Header row: icon + label + spinner + dismiss */}
			<HStack mb={2} spacing={2} justifyContent="space-between" alignItems="center">
				<HStack spacing={2}>
					<Box color="hashtag.fgOnSurface" flexShrink={0}>
						<AiSparkle width={16} height={16} />
					</Box>
					<Text fontSize="xs" fontWeight="semibold" color="hashtag.fg">
						{t(LABEL_MAPPING[dimensionKey] || 'labelSpecific')}
					</Text>
					{isLoading && <Spinner size="xs" color="hashtag.fgOnSurface" data-testid="draft-assist-spinner" />}
				</HStack>
				<IconButton aria-label={t('dismissButton')} icon={<XOutline size="xs" />} variant="ghost" size="xs" color="text.softest" onClick={onDismiss} />
			</HStack>

			{/* Sub-title */}
			<Text fontSize="xs" color="text.softer" mb={1}>
				{t('tryAdding')}
			</Text>

			{/* Suggestion text — full width */}
			<Box minH="1.25rem" mb={3}>
				<AnimatePresence mode="wait">
					<motion.div key="suggestion" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						<Text fontSize="sm" color="text.softer">
							{suggestion}
						</Text>
					</motion.div>
				</AnimatePresence>
			</Box>

			{/* Add button at bottom */}
			<Box display="flex" justifyContent="flex-end">
				<Button size="xs" colorScheme="primary" onClick={onAdd}>
					{t('addButton')}
				</Button>
			</Box>
		</Box>
	);
};
